/**
 * The discount rate's build-up, where the valuation is set to build it: each part - the gilt,
 * the equity risk premium, the relevered industry beta, the size premium, the cost of debt - with
 * its value, whether it was built, supplied or a default, and where it came from. Shown so a
 * reader can argue with the rate rather than take it.
 */

import type { DiscountRateComputed } from '../../../types/financial-model-v2';

const STATUS: Record<string, string> = { built: 'Built', supplied: 'Supplied', default: 'Default' };

function value(name: string, v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return name === 'Beta' ? v.toFixed(2) : `${(v * 100).toFixed(2)}%`;
}

export function DiscountRatePanel({ rate }: { rate: DiscountRateComputed | null | undefined }) {
  if (!rate) return null;
  return (
    <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4" data-testid="discount-rate-panel">
      <h3 className="text-sm font-semibold text-gray-900">Discount rate, built up</h3>
      <p className="mt-0.5 text-xs text-gray-600">
        {rate.wacc !== null && rate.wacc !== undefined
          ? `The valuation discounts at ${value('WACC', rate.wacc)}; residual income and dividends at the cost of equity.`
          : `It could not be built, so the valuation keeps ${value('rate', rate.rate)}.`}
      </p>
      <table className="mt-3 w-full text-xs">
        <tbody className="divide-y divide-gray-100">
          {rate.components.map((c) => (
            <tr key={c.name}>
              <td className="py-1 text-gray-800">{c.name}</td>
              <td className="py-1 text-right font-mono">{value(c.name, c.value)}</td>
              <td className="py-1 pl-3 text-gray-500">{STATUS[c.status] ?? c.status}</td>
              <td className="py-1 pl-3 text-gray-500">{c.source}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rate.missing.length > 0 && (
        <p className="mt-2 text-xs text-amber-700">Missing: {rate.missing.join('; ')}.</p>
      )}
    </section>
  );
}
